/**
 * The Partner Terms version this build shows and asks partners to accept.
 * Sent verbatim to `POST /partner/org` and `POST /partner/terms/accept`; the
 * server refuses any other version with `409 TERMS_OUTDATED`.
 *
 * No DPA version: the DPA is available on request and signed separately
 * (owner decision 2026-09-26), so the click-through covers the Terms alone.
 * `/legal/dpa` is kept as an address only, for the on-request note
 * (pages/LegalPage.tsx), because earlier acceptances and emails link to it.
 */
import type { TermsAcceptance } from '../api/types';
import { CONTRACTOR_FEE_SCHEDULE_DOCUMENT, CONTRACTOR_FEE_SCHEDULE_PATH, CONTRACTOR_FEE_SCHEDULE_VERSION } from './contractor-fee-schedule';
import type { LegalDocument } from './model';
import { PARTNER_TERMS_DOCUMENT, PARTNER_TERMS_VERSION } from './partner-terms';

export const CURRENT_TERMS: TermsAcceptance = { terms_version: PARTNER_TERMS_VERSION };
export const TERMS_PATH = '/legal/partner-terms';
export const DPA_PATH = '/legal/dpa';
export const LEGAL_EMAIL = 'legal@finderlyfix.com';

/**
 * The Contractor Fee Schedule (./contractor-fee-schedule.ts): the written fee
 * agreement Partner Terms §7 refers to. An owner or admin accepts it on the
 * Billing screen, which sends this version verbatim to
 * `POST /partner/billing/fee-schedule/accept`; the server refuses any other
 * with `409 FEE_SCHEDULE_OUTDATED`. Accepting it does not touch the Terms.
 */
export const CURRENT_FEE_SCHEDULE = { version: CONTRACTOR_FEE_SCHEDULE_VERSION } as const;
export const FEE_SCHEDULE_PATH = CONTRACTOR_FEE_SCHEDULE_PATH;
export const BILLING_EMAIL = 'billing@finderlyfix.com';

/**
 * Every legal document the portal publishes, by its `/legal/:doc` slug (the
 * last segment of the document's `path`). The public page (pages/LegalPage.tsx,
 * via LegalDocumentView's `LEGAL_DOCUMENTS`) and the build's Markdown copies
 * (agentSetupFiles.ts → `/legal/<slug>.md`) both read this list. Never the
 * on-request DPA (./dpa.ts), which no app module may import.
 */
export const PUBLISHED_LEGAL_DOCUMENTS: Readonly<Record<string, LegalDocument>> = {
  'partner-terms': PARTNER_TERMS_DOCUMENT,
  'contractor-fee-schedule': CONTRACTOR_FEE_SCHEDULE_DOCUMENT,
};

/** Where the build publishes a document as plain Markdown: `/legal/partner-terms` → `/legal/partner-terms.md`. */
export function legalMarkdownPath(doc: LegalDocument): string {
  return `${doc.path}.md`;
}

/**
 * Organisation → Partner Terms, where an owner or admin opens "Review and
 * accept" (TermsGate). Every "accept the Partner Terms" notice links here.
 */
export const PARTNER_TERMS_ANCHOR = 'partner-terms';
export const PARTNER_TERMS_SETTINGS_PATH = `/settings#${PARTNER_TERMS_ANCHOR}`;

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

/**
 * A legal version id (`partner-terms/2026-09-26d`, `partner-dpa/2026-09-26b`)
 * as a partner reads it: "26 Sep 2026, revision d". The id itself stays the
 * machine value sent to the server; only the display changes. An id that does
 * not follow the `name/YYYY-MM-DD[rev]` shape is shown as-is.
 */
export function legalVersionLabel(id: string): string {
  const m = /\/(\d{4})-(\d{2})-(\d{2})([a-z]?)$/.exec(id);
  if (!m) return id;
  const [, y, mo, d, rev] = m;
  const date = `${Number(d)} ${MONTHS[Number(mo) - 1] ?? mo} ${y}`;
  return rev ? `${date}, revision ${rev}` : date;
}
